/**
 * Stroke masks. A mask is an alpha canvas the size of a layer; strokes stamp opaque paint into it,
 * then the layer is composited `destination-in` the mask.
 *
 * update(R) brings the mask to "points [0, R) stamped". Moving forward stamps only the new points;
 * moving backward clears and re-stamps from 0. Either way the same canvas calls run in the same
 * order, so the pixels are identical (see strokes.ts).
 */

import { makeCanvas, type Ctx2D } from "./canvas";
import type { StrokeSet } from "./strokes";

export class MaskRevealer {
  readonly canvas: OffscreenCanvas;
  private readonly ctx: Ctx2D;
  private readonly isFirst: Uint8Array;
  private drawn = 0;

  constructor(
    w: number,
    h: number,
    private readonly set: StrokeSet,
  ) {
    ({ canvas: this.canvas, ctx: this.ctx } = makeCanvas(w, h));
    this.isFirst = new Uint8Array(set.total);
    for (let k = 0; k < set.strokes; k++) this.isFirst[set.first[k]] = 1;
    this.ctx.fillStyle = "#fff";
    this.ctx.strokeStyle = "#fff";
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
  }

  get revealed(): number {
    return this.drawn;
  }

  update(R: number): void {
    R = Math.max(0, Math.min(this.set.total, R));
    if (R < this.drawn) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.drawn = 0;
    }
    const { x, y, r } = this.set;
    const ctx = this.ctx;
    for (let i = this.drawn; i < R; i++) {
      ctx.beginPath();
      if (this.isFirst[i]) {
        ctx.arc(x[i], y[i], r[i], 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.lineWidth = r[i - 1] + r[i];
        ctx.moveTo(x[i - 1], y[i - 1]);
        ctx.lineTo(x[i], y[i]);
        ctx.stroke();
      }
    }
    this.drawn = R;
  }
}

/** Left-to-right wipe mask, revealed in whole layer-pixel columns. */
export class WipeRevealer {
  readonly canvas: OffscreenCanvas;
  private readonly ctx: Ctx2D;
  private drawn = 0;

  constructor(
    private readonly w: number,
    private readonly h: number,
  ) {
    ({ canvas: this.canvas, ctx: this.ctx } = makeCanvas(w, h));
    this.ctx.fillStyle = "#fff";
  }

  update(cols: number): void {
    cols = Math.max(0, Math.min(Math.ceil(this.w), Math.floor(cols)));
    if (cols < this.drawn) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.drawn = 0;
    }
    if (cols > this.drawn) this.ctx.fillRect(this.drawn, 0, cols - this.drawn, Math.ceil(this.h));
    this.drawn = cols;
  }
}

/** Draw a layer at the size of `target` (the layer's pixel size). A full-size layer is drawn 1:1,
 *  exactly as always; a smaller editor copy of it is stretched to fit. */
export function putLayer(ctx: Ctx2D, layer: CanvasImageSource, target: { width: number; height: number }): void {
  const { width, height } = layer as { width: number; height: number };
  if (width === target.width && height === target.height) ctx.drawImage(layer, 0, 0);
  else ctx.drawImage(layer, 0, 0, target.width, target.height);
}

/** Composite `layer` through `mask` into `scratch` (the layer's size; the layer may be a reduced copy). */
export function maskInto(scratch: Ctx2D, layer: CanvasImageSource, mask: CanvasImageSource): void {
  scratch.save();
  scratch.globalCompositeOperation = "copy";
  putLayer(scratch, layer, scratch.canvas);
  scratch.globalCompositeOperation = "destination-in";
  scratch.drawImage(mask, 0, 0);
  scratch.restore();
}
